import { ComboboxGs, InputGs } from 'components/ui/Forms';
import { useStock } from 'hooks/api/stock/useStock';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ProductOption } from 'contracts/Products';
import { Formatter } from 'utils';

interface LoadFormProps {
  addProduct: (product: any) => void;
  removeProduct: (productId: string) => void;
  productList: any[];
  warehouseId: string;
  companyIds: string;
}

export const LoadForm: React.FC<LoadFormProps> = ({
  addProduct,
  removeProduct,
  productList,
  warehouseId,
  companyIds,
}) => {
  const [productsOptions, setProductsOptions] = useState<ProductOption[]>([]);
  const [quantity, setQuantity] = useState(0);
  const [maxQuantity, setMaxQuantity] = useState(0);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [factor, setFactor] = useState(0);

  const { getStocks } = useStock();

  const filters = {
    formFilter: {
      warehouseId,
      companyIds,
      orderBy: 'entranceDate',
      direction: 'asc',
    },
    statusFilter: ['todos'],
  };

  const {
    isPending: isPendingStock,
    error: errorStock,
    data: stocks,
  } = useQuery({
    queryKey: ['getStocks', filters],
    queryFn: () => getStocks(filters),
    enabled: !!warehouseId && !!companyIds && companyIds.length > 0,
  });

  const handleProductChange = (value: string) => {
    const selectedProduct: any = productsOptions.find(
      (product) => product.value == value
    );
    setSelectedProductId(value);

    setMaxQuantity(selectedProduct ? selectedProduct.quantity : 0);
    setFactor(selectedProduct ? selectedProduct.factor : 0);
    setQuantity(selectedProduct ? selectedProduct.factor : 0);
  };

  const handleAddProduct = (e: any) => {
    e.preventDefault();

    //adicionar produto na lista de selecionados
    const productToAdd = {
      id: selectedProductId,
      code: productsOptions.find(
        (product) => Number(product.value) === Number(selectedProductId)
      )?.code,
      description: productsOptions.find(
        (product) => Number(product.value) === Number(selectedProductId)
      )?.name,
      unit: productsOptions.find(
        (product) => Number(product.value) === Number(selectedProductId)
      )?.unit,
      batch: productsOptions.find(
        (product) => Number(product.value) === Number(selectedProductId)
      )?.batch,
      companyId: productsOptions.find(
        (product) => Number(product.value) === Number(selectedProductId)
      )?.companyId,
      quantity,
    };

    if ((quantity * 100) % (factor * 100) !== 0) {
      toast('Erro', {
        description: `A quantidade informada deve ser múltiplo do fator do produto (${factor})`,
      });
      return;
    }

    if (productToAdd && quantity > 0 && quantity <= maxQuantity) {
      addProduct([productToAdd]);

      //reduzir a quantidade do produto no estoque
      const productOption = productsOptions.find(
        (product) => product.value == selectedProductId
      );

      if (productOption) {
        const newQuantity = productOption.quantity - quantity;
        const newProductOption = { ...productOption, quantity: newQuantity };
        const newProductsOptions = productsOptions.map((product) =>
          Number(product.value) === Number(selectedProductId)
            ? newProductOption
            : product
        );
        setProductsOptions(newProductsOptions);
        setMaxQuantity(newQuantity);
        setQuantity(factor);
      }
    } else {
      toast('Erro', {
        description: 'A quantidade informada excede o estoque disponível',
      });
    }
  };

  const handleRemoveProduct = (productId: string) => {
    //retornar a quantidade do produto ao estoque, caso o produto esteja selecionado, atualizar maxQuantity e quantity
    const productListItem = productList.find(
      (product) => product.id == productId
    );
    const productOption = productsOptions.find(
      (product) => product.value == productId
    );

    if (productOption) {
      const newQuantity = productOption.quantity + productListItem.quantity;
      const newProductOption = { ...productOption, quantity: newQuantity };
      const newProductsOptions = productsOptions.map((product) =>
        product.value == productId ? newProductOption : product
      );

      setProductsOptions(newProductsOptions);

      if (selectedProductId == productId) {
        setMaxQuantity(newQuantity);
        setQuantity(factor);
      }
    }

    removeProduct(productId);
  };

  useEffect(() => {
    if (stocks) {
      const groupedProducts = stocks.reduce(
        (acc: Record<string, ProductOption>, stock: any) => {
          const key = stock.id;
          const name =
            stock.product.name +
            ' - ' +
            stock.company.tradeName +
            (stock.company.addressComplement
              ? ` - ${stock.company.addressComplement}`
              : '') +
            (stock.company.ie ? ` - IE: ${stock.company.ie}` : '') +
            (stock.batch ? ` - LOTE: ${stock.batch}` : '') +
            (stock.expirationDate
                ? ` - VAL.: ${Formatter.date(stock.expirationDate)}`
                : ''
            ) ;
          if (!acc[key]) {
            acc[key] = {
              code: stock.product.code,
              name: name,
              value: stock.id,
              quantity: stock.quantity,
              factor: stock.product.factor,
              unit: stock.productUnit.name,
              batch: stock.batch,
              companyId: stock.company.id,
            };
          } else {
            acc[key].quantity += stock.quantity;
          }
          return acc;
        },
        {}
      );

      const filteredProducts: any = Object.values(groupedProducts).filter(
        (product: any) =>
          product.quantity > 0 && product.quantity >= product.factor
      );
      setProductsOptions(filteredProducts);
    }
  }, [stocks]);

  useEffect(() => {
    if (companyIds.length === 0) setProductsOptions([]);
  }, [companyIds]);

  return (
    <div className="w-full p-6 rounded-sm bg-gray-100 my-1">
      <div>
        <h1 className="font-GilroyBold text-lg">Produtos</h1>
        <p className="font-sans text-sm">
          Selecione os produtos que deseja retirar
        </p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 mt-3 items-end">
        <div className="col-span-1 xl:col-span-8">
          <ComboboxGs
            label="Produto"
            content={productsOptions ?? []}
            fieldValue={selectedProductId}
            fieldName="productId"
            loading={isPendingStock}
            onValueChange={(value) => handleProductChange(value)}
            placeholder={
              warehouseId && companyIds && companyIds.length > 0
                ? 'Selecione um produto'
                : 'Selecione um cliente/armazém'
            }
          />
        </div>
        <div className="col-span-1 xl:col-span-3">
          <InputGs
            name="quantity"
            label={`Quantidade [ ${maxQuantity} em estoque ]`}
            disabled={
              !warehouseId ||
              !companyIds ||
              !selectedProductId ||
              maxQuantity === 0
            }
            required
            type="number"
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.min(Number(e.target.value), maxQuantity))
            }
            min={factor}
            max={maxQuantity}
            step={factor}
          />
        </div>
        <div className="col-span-1">
          <Button
            className="w-full"
            type="button"
            onClick={handleAddProduct}
            disabled={
              !selectedProductId ||
              quantity <= 0 ||
              quantity > maxQuantity ||
              (quantity * 100) % (factor * 100) !== 0 ||
              maxQuantity === 0
            }
          >
            Adicionar
          </Button>
        </div>
      </div>
      {productList.length > 0 && (
        <div className="w-full mt-6">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Código</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Quantidade</TableHead>
                <TableHead>Lote</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productList.map((product) => (
                <TableRow key={product.id}>
                  <TableCell className="font-medium">{product.code}</TableCell>
                  <TableCell className="font-medium">
                    {product.description}
                  </TableCell>
                  <TableCell className="font-medium">
                    {product.quantity} {product.unit}
                  </TableCell>
                  <TableCell className="font-medium">{product.batch}</TableCell>
                  <TableCell className="flex justify-end">
                    <Trash2
                      className="text-red-600 cursor-pointer"
                      onClick={() => handleRemoveProduct(product.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
